if (!customElements.get('wishlist-products')) {
  class WishlistProducts extends HTMLElement {
    constructor() {
      super();

      this.grid = this.querySelector('.main-wishlist__grid');
      this.emptyState = this.querySelector('.main-wishlist__empty');
      this.setupAlertsBanner();

      this.fetchProducts();

      document.addEventListener('wishlist:updated', this.handleUpdate.bind(this));
    }

    async fetchProducts() {
      if (!window.Shopify || !window.Shopify.routes || !window.Shopify.routes.root) {
        setTimeout(() => this.fetchProducts(), 100);
        return;
      }

      // Signed in: take the account's list (saved on any device) first. Bounded,
      // so a slow app proxy never holds the page.
      const toggle = window.WishlistToggle;
      if (toggle && typeof toggle.syncAccount === 'function') {
        await Promise.race([
          toggle.syncAccount({ force: true }),
          new Promise(resolve => setTimeout(resolve, 4000))
        ]);
      }

      const stored = localStorage.getItem('wishlist');
      const handles = stored
        ? stored.split(',').map(h => h.trim()).filter(h => h && h !== 'undefined')
        : [];

      if (handles.length === 0) {
        this.showEmptyState();
        return;
      }

      const results = await Promise.all(handles.map(handle => this.renderCard(handle)));

      const validHandles = [];
      const validHtml = [];

      results.forEach((html, i) => {
        if (html) {
          validHandles.push(handles[i]);
          validHtml.push(html);
        }
      });

      if (validHtml.length === 0) {
        localStorage.removeItem('wishlist');
        this.showEmptyState();
        return;
      }

      if (validHandles.length !== handles.length) {
        localStorage.setItem('wishlist', validHandles.join(','));
      }

      validHtml.forEach(html => this.grid.insertAdjacentHTML('beforeend', html));
      this.grid.classList.remove('hidden');
      this.showAlertsBanner(true);

      if (window.WishlistToggle && typeof window.WishlistToggle.sync === 'function') {
        window.WishlistToggle.sync(this.grid);
      }
    }

    async renderCard(handle) {
      try {
        const response = await fetch(`${window.Shopify.routes.root}products/${handle}?view=card`);
        if (!response.ok) return null;

        const html = await response.text();
        if (!html.trim()) return null;

        return html;
      } catch (e) {
        return null;
      }
    }

    showEmptyState() {
      this.emptyState.classList.remove('hidden');
      this.grid.classList.add('hidden');
      this.showAlertsBanner(false);
    }

    handleUpdate(event) {
      const handles = event.detail && event.detail.handles ? event.detail.handles : [];
      const cards = this.grid.querySelectorAll('[data-wishlist-handle]');
      let removed = false;

      cards.forEach(btn => {
        const handle = btn.dataset.wishlistHandle;
        if (!handles.includes(handle)) {
          const card = btn.closest('product-card') || btn.closest('.product-card') || btn.closest('.swiper-slide');
          if (card) {
            card.remove();
            removed = true;
          }
        }
      });

      if (removed && this.grid.querySelectorAll('[data-wishlist-handle]').length === 0) {
        this.showEmptyState();
      }
    }

    // Guest banner: "get notified about your saved pieces" — the same email the ♡
    // prompt asks for (WishlistToggle.submitEmail), shown while the list has items
    // and no email is known yet.
    setupAlertsBanner() {
      this.banner = this.querySelector('[data-wishlist-alerts-banner]');
      if (!this.banner) return;
      this.bannerForm = this.banner.querySelector('form');
      this.bannerInput = this.bannerForm.elements.email;
      this.bannerSubmit = this.bannerForm.querySelector('[type="submit"]');
      this.bannerStatus = this.banner.querySelector('.main-wishlist__alerts-status');
      this.bannerDone = this.banner.querySelector('.main-wishlist__alerts-done');
      this.bannerForm.addEventListener('submit', this.onBannerSubmit.bind(this));
    }

    showAlertsBanner(show) {
      if (!this.banner) return;
      const toggle = window.WishlistToggle;
      const known = Boolean(toggle && typeof toggle.hasAlertsEmail === 'function' && toggle.hasAlertsEmail());
      // Once submitted here, keep the confirmation on screen.
      this.banner.classList.toggle('hidden', !show || (known && !this.bannerSubmitted));
    }

    showBannerStatus(message) {
      this.bannerStatus.textContent = message || '';
      this.bannerStatus.hidden = false;
      this.bannerInput.setAttribute('aria-invalid', 'true');
    }

    async onBannerSubmit(event) {
      event.preventDefault();
      const toggle = window.WishlistToggle;
      if (!toggle || typeof toggle.submitEmail !== 'function') return;
      // Honeypot: real shoppers never fill it.
      if (this.bannerForm.elements.website && this.bannerForm.elements.website.value) return;

      const email = (this.bannerInput.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        this.showBannerStatus(this.banner.dataset.textInvalidEmail);
        this.bannerInput.focus();
        return;
      }
      this.bannerStatus.hidden = true;
      this.bannerInput.removeAttribute('aria-invalid');
      this.bannerSubmit.setAttribute('aria-busy', 'true');
      this.bannerSubmit.disabled = true;
      const result = await toggle.submitEmail(email);
      this.bannerSubmit.removeAttribute('aria-busy');
      this.bannerSubmit.disabled = false;

      if (!result.ok) {
        this.showBannerStatus(
          result.error === 'invalid_email' ? this.banner.dataset.textInvalidEmail : this.banner.dataset.textError
        );
        this.bannerInput.focus();
        return;
      }
      this.bannerSubmitted = true;
      toggle.fillWithEmail(this.bannerDone, this.banner.dataset.textSuccess, result.email);
      this.bannerForm.hidden = true;
      this.bannerDone.hidden = false;
    }
  }

  customElements.define('wishlist-products', WishlistProducts);
}
